import {
  FocusEvent,
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import ReactDatePicker, {
  ReactDatePickerCustomHeaderProps,
  ReactDatePickerProps,
  registerLocale,
} from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
// import "../../../../apps/deal-central/src/styles/reactdatepicker.css";
/* eslint-disable import/no-duplicates */
import { format, setMonth, getMonth, Locale } from "date-fns";
import { enCA, frCA } from "date-fns/locale";
/* eslint-enable import/no-duplicates */
import { BaseInputProps } from "@talladega/types";
import {
  CalendarTodayIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@talladega/icons";
import {
  useWindowDimensions,
  useDevice,
  useDateMask,
  useDateTimeMask,
  useMonthMask,
} from "@talladega/hooks";
import classNames from "classnames";
import { Form } from "../FormInputs";

registerLocale("en", enCA);
registerLocale("fr", frCA);

const smallDeviceCutoff = 667;

const dateToday = new Date();

const getRange = (
  lowEnd: Date | null | undefined,
  highEnd: Date,
  increment: number
) => {
  const years = [];
  const max = highEnd.getFullYear();
  let min = 0;

  if (lowEnd !== undefined) {
    min = lowEnd?.getFullYear() || 0;
  } else {
    min = dateToday.getFullYear() - 1;
  }

  for (let i = min; i <= max; i += increment) {
    years.push(i);
  }

  return years;
};

type CustomInputProps = BaseInputProps;

const CustomInput = forwardRef<HTMLInputElement, CustomInputProps>(
  ({ id = "datePicker", value, onClick, className, ...props }, ref) => {
    return (
      <Form.Input
        id={id}
        type="text"
        rightIcon={<CalendarTodayIcon />}
        onClick={onClick}
        value={value}
        ref={ref}
        {...props}
      />
    );
  }
);

type DatePickerProps = Pick<
  ReactDatePickerProps<string, boolean>,
  | "minDate"
  | "maxDate"
  | "excludeDates"
  | "selected"
  | "onChange"
  | "showTimeInput"
  | "showMonthYearPicker"
  | "showFullMonthYearPicker"
  | "popperPlacement"
  | "filterDate"
  | "placeholderText"
  | "locale"
  | "monthsShown"
  | "selectsRange"
  | "calendarContainer"
  | "startDate"
  | "endDate"
  | "shouldCloseOnSelect"
  | "onCalendarClose"
  | "onCalendarOpen"
  | "calendarStartDay"
> &
  Pick<
    CustomInputProps,
    | "id"
    | "label"
    | "disabled"
    | "status"
    | "feedback"
    | "hint"
    | "suppressHotjar"
  > & {
    iconPlacement?: "left" | "right";
  };

type DatePickerCustomHeaderProps = {
  minDate?: Date | null;
  maxDate?: Date | null;
  locale?: string | Locale;
  selectsRange?: boolean;
  showMonthYearPicker?: boolean;
} & ReactDatePickerCustomHeaderProps;

const customHeader = ({
  date,
  changeYear,
  changeMonth,
  decreaseMonth,
  increaseMonth,
  decreaseYear,
  increaseYear,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
  minDate,
  maxDate,
  locale = "en",
  showMonthYearPicker,
}: DatePickerCustomHeaderProps): React.ReactNode => {
  const years = getRange(minDate, maxDate || dateToday, 1);
  const months = [...Array(12)].map((_, i) => ({
    value: i,
    label: format(setMonth(new Date(), i), "MMMM", {
      locale: locale === "en" ? enCA : frCA,
    }),
  }));

  return (
    <div
      className={classNames("custom-date-header", {
        "px-5": showMonthYearPicker,
      })}
    >
      <button
        type="button"
        onClick={showMonthYearPicker ? decreaseYear : decreaseMonth}
        aria-label="Previous Month"
        disabled={prevMonthButtonDisabled}
      >
        <ChevronLeftIcon className="h-5 w-5 text-gray-600" />
      </button>

      <select
        value={date.getFullYear()}
        onChange={({ target: { value } }) => changeYear(parseInt(value, 10))}
        className={classNames("custom-date-header-select ml-1 h-12", {
          "rounded-lg": showMonthYearPicker,
          "rounded-l-lg": !showMonthYearPicker,
        })}
        aria-label="Select year"
      >
        {years.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>

      {!showMonthYearPicker && (
        <select
          value={getMonth(date)}
          onChange={({ target: { value } }) => changeMonth(parseInt(value, 10))}
          className="custom-date-header-select mr-1 rounded-r-lg capitalize h-12"
          aria-label="Select month"
        >
          {months.map(({ value, label }) => (
            <option key={value} value={value} className="m-1">
              {label}
            </option>
          ))}
        </select>
      )}

      <button
        type="button"
        onClick={showMonthYearPicker ? increaseYear : increaseMonth}
        aria-label="Next Month"
        disabled={nextMonthButtonDisabled}
      >
        <ChevronRightIcon className="h-5 w-5 text-gray-600" />
      </button>
    </div>
  );
};

const customRangeHeader = ({
  monthDate,
  decreaseMonth,
  increaseMonth,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
  locale = "en",
}: DatePickerCustomHeaderProps): React.ReactNode => {
  const monthLabel = format(monthDate, "MMMM", {
    locale: locale === "en" ? enCA : frCA,
  });

  return (
    <div className="flex justify-between items-center p-3">
      <button
        type="button"
        onClick={decreaseMonth}
        aria-label="Previous Month"
        disabled={prevMonthButtonDisabled}
      >
        <ChevronLeftIcon className="h-5 w-5 text-secondary" />
      </button>

      <p>
        <span className="font-semibold capitalize h-12 text-secondary text-base">
          {monthLabel} {monthDate.getFullYear()}
        </span>
      </p>

      <button
        type="button"
        onClick={increaseMonth}
        aria-label="Next Month"
        disabled={nextMonthButtonDisabled}
      >
        <ChevronRightIcon className="h-5 w-5 text-secondary" />
      </button>
    </div>
  );
};

export const DatePicker = forwardRef<
  { focus: () => void; setOpen: (open: boolean) => void },
  DatePickerProps
>(
  (
    {
      id = "datepicker-custom-input",
      minDate,
      maxDate,
      showTimeInput = false,
      showMonthYearPicker = false,
      showFullMonthYearPicker = false,
      label,
      disabled,
      status,
      feedback = "",
      hint,
      popperPlacement,
      onChange,
      locale,
      suppressHotjar,
      iconPlacement = "right",
      monthsShown,
      selectsRange,
      calendarContainer,
      startDate,
      endDate,
      shouldCloseOnSelect = true,
      onCalendarClose,
      onCalendarOpen,
      calendarStartDay,
      ...props
    }: DatePickerProps,
    ref
  ) => {
    const maskDate = useDateMask(minDate, maxDate);
    const maskDateTime = useDateTimeMask(minDate, maxDate);
    const maskMonth = useMonthMask(minDate, maxDate);

    let dateFormat: string;
    if (showMonthYearPicker) {
      dateFormat = "MMMM yyyy";
    } else if (showTimeInput) {
      dateFormat = "MM/dd/yyyy hh:mm aa";
    } else {
      dateFormat = "MM/dd/yyyy";
    }

    const handleChangeRaw = (e: FocusEvent<HTMLInputElement, Element>) => {
      if (!e.target.value) return;

      let mask;
      if (showMonthYearPicker) {
        mask = maskMonth;
      } else if (showTimeInput) {
        mask = maskDateTime;
      } else {
        mask = maskDate;
      }

      const valueRaw = e.target.value;

      const maskedValue = mask.maskPipe(valueRaw);

      if (!maskedValue || maskedValue.length < valueRaw.length) {
        e.target.value = valueRaw;
      } else {
        e.target.value = maskedValue;
      }
    };

    const datePickerRef = useRef<ReactDatePicker>(null);

    useImperativeHandle(ref, () => {
      return {
        focus: () => {
          datePickerRef.current?.setFocus();
        },
        setOpen: (open: boolean) => {
          datePickerRef.current?.setOpen(open);
        },
      };
    });

    const [showVirtualKeyboard, setShowVirtualKeyboard] = useState(false);
    const [datePickerIsOpen, setDatePickerIsOpen] = useState(false);
    const device = useDevice();

    const { height: windowHeight } = useWindowDimensions();
    const isSmallDevice = windowHeight <= smallDeviceCutoff;

    return (
      <div className="[&>div]:w-full">
        <ReactDatePicker
          portalId="rr-datepicker-popper"
          timeInputLabel="Time:"
          dateFormat={dateFormat}
          calendarClassName="date-picker-wrapper"
          disabledKeyboardNavigation
          showTimeInput={showTimeInput}
          showMonthYearPicker={showMonthYearPicker}
          showFullMonthYearPicker={showFullMonthYearPicker}
          popperPlacement={popperPlacement}
          minDate={minDate}
          maxDate={maxDate}
          autoComplete="on"
          ref={datePickerRef}
          id={id}
          onCalendarOpen={onCalendarOpen}
          onCalendarClose={() => {
            setShowVirtualKeyboard(false);
            onCalendarClose?.();
          }}
          onSelect={() => {
            setShowVirtualKeyboard(false);
            setDatePickerIsOpen(false);
          }}
          onClickOutside={(e) => {
            const targetEl = e.target as HTMLElement;
            // If the click is outside the datepicker AND the associated input, close it
            if (targetEl.id !== id) {
              setShowVirtualKeyboard(false);
              setDatePickerIsOpen(false);
            }
          }}
          onFocus={(e) => {
            const { top, bottom } = e.target.getBoundingClientRect();
            if (bottom > windowHeight || top < 0) {
              e.target.scrollIntoView({ block: "start" });
            }
          }}
          onInputClick={() => {
            /**
             * To prevent date picker being cutoff on smaller devices, we hide the virtual keyboard and open the date picker on input click
             * When user click the input again, we show the virtual keyboard
             *
             * TODO: There is no clear way for user to know about this. So there might be a better way to do this.
             */
            if (datePickerIsOpen) {
              setShowVirtualKeyboard(true);
            } else {
              setDatePickerIsOpen(true);
            }

            if (!isSmallDevice) {
              const datepickerPopper = document.querySelector(
                ".react-datepicker-popper"
              );

              // scroll pop-up into view if it's cutoff
              if (datepickerPopper) {
                setTimeout(
                  () => {
                    const { top, bottom } =
                      datepickerPopper.getBoundingClientRect();
                    if (bottom > windowHeight || top < 0) {
                      datepickerPopper.scrollIntoView({ block: "start" });
                    }
                  },
                  // enough time for virtual keyboard to open
                  device?.isMobile ? 600 : 0
                );
              }
            }
          }}
          customInput={
            <CustomInput
              // "none" inputMode will disable virtual keyboard on mobile, defaults to undefined
              inputMode={
                (device?.isMobile && isSmallDevice && showVirtualKeyboard) ||
                (device?.isMobile && !isSmallDevice) ||
                device?.isDesktop
                  ? undefined
                  : "none"
              }
              autoComplete="on"
              id={id}
              label={label}
              disabled={disabled}
              status={status}
              feedback={feedback}
              hint={hint}
              suppressHotjar={suppressHotjar}
              leftIcon={
                iconPlacement === "left" ? <CalendarTodayIcon /> : undefined
              }
              rightIcon={
                iconPlacement === "right" ? <CalendarTodayIcon /> : undefined
              }
            />
          }
          renderCustomHeader={(customHeaderProps) =>
            selectsRange
              ? customRangeHeader({
                  ...customHeaderProps,
                  locale,
                })
              : customHeader({
                  ...customHeaderProps,
                  minDate,
                  maxDate,
                  locale,
                  showMonthYearPicker,
                })
          }
          monthsShown={monthsShown}
          selectsRange={selectsRange}
          startDate={startDate}
          endDate={endDate}
          calendarContainer={calendarContainer}
          onChange={onChange}
          onChangeRaw={handleChangeRaw}
          locale={locale}
          shouldCloseOnSelect={shouldCloseOnSelect}
          calendarStartDay={calendarStartDay}
          {...props}
        />
      </div>
    );
  }
);

export default {};
